import { useQuery } from "@tanstack/react-query";
import { Loader2, Star } from "lucide-react";
import type { MatchDto, MatchTipDto } from "@/types/api";
import { matchesApi } from "@/api/matches";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

function pct(n: number, total: number): string {
  return total === 0 ? "0%" : `${Math.round((n / total) * 100)}%`;
}

export function OtherTipsModal({ match, onClose }: { match: MatchDto | null; onClose: () => void }) {
  const scope = useScope();
  const q = useQuery({
    queryKey: ["matchTips", match?.id, scope.leagueId, scope.tournamentId],
    queryFn: () => matchesApi.matchTips(match!.id, scope),
    enabled: !!match,
  });

  return (
    <Dialog open={!!match} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        {match && (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                <span className="inline-flex items-center gap-1.5">
                  <TeamFlag name={match.teamAName} flag={match.teamAFlag} className="size-5" />
                  {match.teamAName}
                </span>
                <span className="font-bold tabular-nums">
                  {match.scoreA}–{match.scoreB}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  {match.teamBName}
                  <TeamFlag name={match.teamBName} flag={match.teamBFlag} className="size-5" />
                </span>
              </DialogTitle>
            </DialogHeader>

            <DialogBody className="space-y-4">
              {q.isLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {q.data && <TipsContent tips={q.data.tips} scoreA={q.data.scoreA} scoreB={q.data.scoreB} />}
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TipsContent({ tips, scoreA, scoreB }: { tips: MatchTipDto[]; scoreA: number | null; scoreB: number | null }) {
  const total = tips.length;
  const bullseye = tips.filter((t) => t.predictedA === scoreA && t.predictedB === scoreB).length;
  const outcome = tips.filter(
    (t) =>
      t.predictedA !== null &&
      t.predictedB !== null &&
      scoreA !== null &&
      scoreB !== null &&
      Math.sign(t.predictedA - t.predictedB) === Math.sign(scoreA - scoreB),
  ).length;
  const avgGoals =
    total === 0
      ? 0
      : tips.reduce((s, t) => s + (t.predictedA ?? 0) + (t.predictedB ?? 0), 0) / total;

  const popularity = new Map<string, number>();
  for (const t of tips) {
    if (t.predictedA === null || t.predictedB === null) continue;
    const k = `${t.predictedA}–${t.predictedB}`;
    popularity.set(k, (popularity.get(k) ?? 0) + 1);
  }
  const top = [...popularity.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatTile label="Telitalálat" value={pct(bullseye, total)} />
        <StatTile label="Kimenetel" value={pct(outcome, total)} />
        <StatTile label="Átlag gól" value={avgGoals.toFixed(1)} />
        <StatTile label="Tippelők" value={String(total)} />
      </div>

      {top.length > 0 && (
        <div>
          <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Legnépszerűbb tippek</h4>
          <div className="flex flex-wrap gap-1.5">
            {top.map(([k, n]) => (
              <span key={k} className="rounded-full border border-border px-2 py-0.5 text-xs tabular-nums">
                {k} · {n}×
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Összes tipp</h4>
        <ul className="divide-y divide-border/60">
          {tips.map((t, i) => (
            <li key={i} className={cn("flex items-center gap-2 py-1.5 text-sm", t.isTuti && "text-warning")}>
              {t.isTuti && <Star className="size-3.5 shrink-0 fill-warning text-warning" />}
              <span className="min-w-0 flex-1 truncate">{t.name}</span>
              <span className="tabular-nums text-muted-foreground">
                {t.predictedA ?? "–"}–{t.predictedB ?? "–"}
              </span>
              <span className="w-14 text-right font-medium tabular-nums">{t.points} pont</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-background/40 p-2 text-center">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
